/**
 * Card readers: the machines guests pay on, and taking a card on one.
 *
 * `payments.terminal`. Any card machine works: the till shows the amount,
 * staff key it on the machine and press what it showed. Nothing is taken
 * until "Approved", and pressing it twice takes it once — the server keys the
 * payment on the attempt.
 *
 * A reader is not a till. A till is a `terminal`; a reader is the machine a
 * guest taps a card on, and one can serve several tills. Which reader this
 * till uses is remembered on the device (`readerForThisTill`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import type { Bill } from './payments';
import { useApi } from './provider';

export interface CardReader {
  id: string;
  location_id: string;
  name: string;
  /** Which adapter drives it. `any`: any card machine, keyed by hand. */
  provider: string;
  retired_at: string | null;
}

export type CardAttemptState = 'waiting' | 'approved' | 'declined' | 'cancelled';

export interface CardAttempt {
  id: string;
  order_id: string;
  reader_id: string;
  reader_name: string;
  amount: string;
  tip: string;
  state: CardAttemptState;
  /** `key_in`: somebody keys the amount on the machine and reports back. */
  prompt_kind: string;
  prompt: string;
  answered_by_hand: boolean;
  auth_code: string | null;
  card_last4: string | null;
  payment_id: string | null;
}

export interface ReaderTakings {
  /** Null for card payments typed in without a reader. */
  reader_id: string | null;
  reader_name: string;
  count: number;
  taken: string;
  tips: string;
  refunded: string;
  declined: number;
}

export function useCardReaders(
  locationId: string | undefined,
  options?: { enabled?: boolean },
) {
  const api = useApi();
  return useQuery({
    queryKey: ['payments', 'card-readers', locationId],
    queryFn: ({ signal }) =>
      api.get<CardReader[]>(`/v1/payments/card-readers?location_id=${locationId}`, signal),
    enabled: Boolean(locationId) && (options?.enabled ?? true),
  });
}

export function useAddCardReader() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<CardReader, ApiError, { location_id: string; name: string }>({
    mutationFn: (body) => api.post<CardReader>('/v1/payments/card-readers', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payments', 'card-readers'] });
    },
  });
}

export function useChangeCardReader() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<CardReader, ApiError, { id: string; name?: string; retired?: boolean }>({
    mutationFn: ({ id, ...body }) =>
      api.request<CardReader>(`/v1/payments/card-readers/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['payments', 'card-readers'] });
    },
  });
}

/** A trading day's card takings, one line per reader. */
export function useReaderTakings(
  locationId: string | undefined,
  businessDate: string | undefined,
) {
  const api = useApi();
  return useQuery({
    queryKey: ['payments', 'reader-takings', locationId, businessDate],
    queryFn: ({ signal }) =>
      api.get<ReaderTakings[]>(
        `/v1/payments/card-readers/takings?location_id=${locationId}&business_date=${businessDate}`,
        signal,
      ),
    enabled: Boolean(locationId && businessDate),
  });
}

export interface StartCardInput {
  orderId: string;
  reader_id: string;
  amount: string;
  tip?: string;
  terminal_id?: string;
  line_ids?: string[];
}

/** Ask a reader for a card payment. Nothing is taken yet. */
export function useStartCard() {
  const api = useApi();
  return useMutation<CardAttempt, ApiError, StartCardInput>({
    mutationFn: ({ orderId, ...body }) =>
      api.post<CardAttempt>(`/v1/payments/bills/${orderId}/card`, body),
  });
}

/** The reader approved it: the payment is recorded, once. */
export function useApproveCard() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Bill,
    ApiError,
    { attemptId: string; auth_code?: string; card_last4?: string }
  >({
    mutationFn: ({ attemptId, ...body }) =>
      api.post<Bill>(`/v1/payments/card-attempts/${attemptId}/approve`, body),
    onSuccess: (bill) => {
      // The same refreshes as any payment (`useTakePayment`): the bill, the
      // splits of what is left, and the tab, table and kitchen it closes.
      queryClient.setQueryData(['payments', 'bill', bill.order_id], bill);
      void queryClient.invalidateQueries({ queryKey: ['payments', 'split'] });
      void queryClient.invalidateQueries({ queryKey: ['payments', 'split-parts'] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: ['floor'] });
      void queryClient.invalidateQueries({ queryKey: ['kds'] });
    },
  });
}

/** The reader declined it (`declined: true`), or the till gave up on it. */
export function useCloseCard() {
  const api = useApi();
  return useMutation<CardAttempt, ApiError, { attemptId: string; declined: boolean }>({
    mutationFn: ({ attemptId, declined }) =>
      api.post<CardAttempt>(
        `/v1/payments/card-attempts/${attemptId}/${declined ? 'decline' : 'cancel'}`,
      ),
  });
}

const READER_KEY = 'restaurant.till.card-reader';

/** Which reader this till sends card payments to, remembered on the device:
 *  the machine beside a till does not change with whoever signs in. */
export function readerForThisTill(): string | null {
  try {
    return localStorage.getItem(READER_KEY);
  } catch {
    return null;
  }
}

export function setReaderForThisTill(readerId: string | null): void {
  try {
    if (readerId) localStorage.setItem(READER_KEY, readerId);
    else localStorage.removeItem(READER_KEY);
  } catch {
    // A device that will not store it asks again next time; nothing breaks.
  }
}
